import { useMutationSummarization } from "@apis:summarization/summarization";
import { ActionButton } from "@components:ActionButton";
import {
  alpha,
  Box,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SectionHeading } from "@pages:app/audio/components/SectionHeading";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCancel, MdOutlineSummarize } from "react-icons/md";

interface SummaryPanelProps {
  transcript: string;
  botId: string;
  summary: string;
  onSummarized: (summary: string) => void;
}

/*
 * The summary column: a button to summarize, then the summary itself.
 * Rendered only when the bot has both audio and summarization configured.
 */
export const SummaryPanel = ({
  transcript,
  botId,
  summary,
  onSummarized,
}: SummaryPanelProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [error, setError] = useState<string>("");

  const summarization = useMutationSummarization({
    onSuccess: (data) => {
      onSummarized(data);
      setError("");
    },
    onError: (err) =>
      setError(
        `${t("audio.summary.failed")} ${get(
          err,
          "response.data.detail",
          t("audio.summary.tryAgain"),
        )}`,
      ),
  });

  const paragraphs = summary
    .trim()
    .split(/\n{2,}/)
    .filter(Boolean);

  const summarize = () => {
    if (!transcript.trim()) return;
    summarization.mutate({ type: "text", text: transcript, botId });
  };

  const renderCard = () => {
    if (summarization.isPending) {
      return (
        <Box aria-live="polite">
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
            {t("audio.summary.summarizing")}
          </Typography>
          {["100%", "96%", "88%", "60%"].map((width) => (
            <Skeleton
              key={width}
              variant="text"
              width={width}
              sx={{ fontSize: 15 }}
            />
          ))}
        </Box>
      );
    }

    if (summary) {
      return paragraphs.map((paragraph, index) => (
        <Typography
          key={index}
          sx={{
            fontSize: 15,
            lineHeight: 1.6,
            whiteSpace: "pre-line",
            mb: index < paragraphs.length - 1 ? 1.5 : 0,
          }}
        >
          {paragraph}
        </Typography>
      ));
    }

    return (
      <Stack spacing={2} alignItems="flex-start">
        <Typography sx={{ fontSize: 15, lineHeight: 1.6 }}>
          {t("audio.summary.empty")}
        </Typography>
        <ActionButton
          onClick={summarize}
          disabled={!transcript.trim()}
          startIcon={<MdOutlineSummarize />}
        >
          {t("audio.summary.summarize")}
        </ActionButton>
      </Stack>
    );
  };

  return (
    <Box component="section" aria-labelledby="summary-heading">
      <SectionHeading id="summary-heading">
        {t("audio.summary.title")}
      </SectionHeading>

      <Box>
        <Box
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: "20px",
            px: 2.5,
            py: 2.25,
          }}
        >
          {renderCard()}
        </Box>

        {error && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ color: "error.main", mt: 1.5 }}
            role="alert"
          >
            <MdCancel />
            <Typography variant="body2">{error}</Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};
